/* ────────────────────────────────────────────────────────────
   src/components/DynamicRoutes.jsx
────────────────────────────────────────────────────────────── */
import React, { useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import PrivateRoute from './PrivateRoute';
import Layout from '../components/Layout/Layout';
import componentsMap from '../components/componentsMap';
import useDynamicModules from '../components/useDynamicModules';
import { useAuth } from '../context/AuthContext';
import SupportPage from '../pages/SupportPage';

/* static pages & layouts */
import TopNavLayout from '../components/Layout/TopNavLayout';
//import LandingPage from '../pages/LandingPage';
import AboutUsPage from '../pages/AboutUsPage';
import FeaturesPage from '../pages/FeaturesPage';
import PricingPage from '../pages/PricingPage';
import ContactUsPage from '../pages/ContactUsPage';
import Login from '../pages/Login';
import HotelForm from '../pages/HotelForm';
import EmailConfirmation from '../pages/EmailConfirmation';
import ResendConfirmationPage from '../pages/ResendConfirmationPage';
import EmailConfirmationPendingPage from '../pages/EmailConfirmationPendingPage';
import Unauthorized from '../pages/Unauthorized';
import ResetPassword from '../pages/ResetPassword';
import ForgotPassword from '../pages/ForgotPassword';
import ModuleAssignment from '../pages/ModuleAssignment';
import RoomQRPage from '../pages/RoomQRPage';
import AdminRegistrationPage from '../pages/AdminRegistrationPage';
import RestaurantOrderPage from '../pages/RestaurantOrderPage';
import CreateServicePage from '../pages/CreateServicePage';
import SetupHotelPage from '../pages/SetupHotelPage';
import KYCUploadPage from '../pages/KYCUploadPage';
import KycCapturePage from '../pages/KycCapturePage';
import KYCVerificationPage from '../pages/KYCVerificationPage';
import CssEditorPage from '../pages/admin/CssEditorPage';

import LandingPageDb from '../pages/LandingPageDb';
import LandingPage from '../pages/LandingPage';
import LandingHtmlEditor from '../pages/admin/LandingHtmlEditor';  
import SupportAdminPage from '../pages/admin/SupportAdminPage';
import HotelEmailsPage from '../pages/HotelEmailsPage';

import MySettingsPage from "../pages/MySettingsPage";

import HousekeepingChecklistTemplatesPage from "../pages/HousekeepingChecklistTemplatesPage";

import TermsOfUsePage from "../pages/TermsOfUsePage";
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage";


/*normalize role strings from token/API for module filtering only */
const normalizeRole = (role) => {
  if (!role) return '';
  try {
    const r = String(role).toLowerCase().trim();
    if (['owner', 'hotelowner'].includes(r)) return 'owner';
    return r; // no other special-casing
  } catch {
    return '';
  }
};

const DynamicRoutes = () => {
  /* 1) auth + dynamic modules */
  const { authData, loading: authLoading } = useAuth();
  const { data: modules = [], isLoading, error } = useDynamicModules();

  /* 2) role list from token (for filtering server-provided modules) */
  const normalizedRoles = useMemo(() => {
    const raw = Array.isArray(authData?.roles)
      ? authData.roles
      : authData?.role
      ? [authData.role]
      : [];
    return raw.map(normalizeRole).filter(Boolean);
  }, [authData]);

  /* 3) while auth hydrating */
  if (authLoading) {
    return (
      <div className="loading-spinner">
        <div className="spinner" />
        <p>Loading app...</p>
      </div>
    );
  }

  /* ───────────── PUBLIC (no token) ───────────── */
  if (!authData?.token) {
    return (
      <Routes>
        <Route path="/admin/cms/landing" element={<LandingHtmlEditor />} />
        <Route path="/devMode" element={<LandingPageDb />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<AboutUsPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/contact" element={<ContactUsPage />} />

        <Route path="/legal/terms" element={<TermsOfUsePage />} />
        <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />

        <Route path="/register" element={<HotelForm />} />
        <Route path="/User-register" element={<HotelForm />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/email-confirmation" element={<EmailConfirmation />} />
        <Route path="/email-confirmation-pending" element={<EmailConfirmationPendingPage />} />
        <Route path="/resend-confirmation" element={<ResendConfirmationPage />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/Reset-Password" element={<ResetPassword />} />
        <Route path="/Forgot-Password" element={<ForgotPassword />} />
        <Route path="/roomservicerequest" element={<RoomQRPage />} />
        <Route path="/module-assignment" element={<ModuleAssignment />} />
        <Route path="/admin/reg" element={<AdminRegistrationPage />} />
        <Route path="/hotels/Restaurant" element={<RestaurantOrderPage />} />
        <Route path="/hotels/CreateService" element={<CreateServicePage />} />
        <Route path="/kyc/phone-capture/:sessionId" element={<KycCapturePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  /* dynamic modules state */
  if (isLoading) {
    return (
      <div className="loading-spinner">
        <div className="spinner" />
        <p>Loading ...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="error-message">
        <h3>Error loading routes</h3>
        <p>{error.message}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  /* ───────────── AUTHENTICATED ───────────── */
  return (
    <Routes>
      {/* public pages still accessible after login 
      <Route path="/" element={<LandingPage />} />
      */}
       
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactUsPage />} />

      {/* ── private zone ── */}
      <Route element={<PrivateRoute />}>
        {/* everything private lives under the main layout */}
        <Route element={<Layout />}>
          {/* server-driven modules */}
          {modules.map((mod) => {
            const Cmp = componentsMap[mod.component];
            if (!Cmp) return null;

            // optional server-side role filtering per module
            if (mod.AllowedRoles) {
              try {
                const needed = String(mod.AllowedRoles)
                  .split(',')
                  .map((r) => normalizeRole(r.trim()))
                  .filter(Boolean);
                if (needed.length && !needed.some((r) => normalizedRoles.includes(r))) {
                  return null; // user not allowed for this module
                }
              } catch (e) {
                console.error('Error processing module roles:', e);
                return null;
              }
            }

            const path = mod.path?.startsWith('/') ? mod.path.slice(1) : mod.path;
            if (!path) return null;

            return <Route key={mod.moduleId || mod.path} path={path} element={<Cmp />} />;
          })}

          <Route path="hotel/housekeeping/checklists" element={<HousekeepingChecklistTemplatesPage />} />

          <Route path="hotel/my/settings" element={<MySettingsPage />} />
          <Route path="hotel/emails" element={<HotelEmailsPage />} />
          
           <Route path="/legal/terms" element={<TermsOfUsePage />} />
          <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
          {/* default fallback inside layout */}
          <Route path="*" element={<Navigate to="/hotels/dashboard" replace />} />

          
        </Route>

        {/* owner-only flows: KYC + initial setup (TopNav layout) */}
        <Route element={<PrivateRoute ownerOnly />}>
          <Route element={<TopNavLayout />}>
            <Route path="/kyc-upload" element={<KYCUploadPage />} />
            
            <Route path="/kyc/phone-capture/:sessionId" element={<KycCapturePage />} />
            <Route path="/kyc-verification" element={<KYCVerificationPage />} />
            <Route path="/setup-hotel" element={<SetupHotelPage />} />
          </Route>
        </Route>

        {/* example: pages that require an explicit permission flag from API (optional)
            If you still want a sysadmin-only tool, consider checking a permission
            claim from your token instead of hard-coded role names. For now it’s open. */}
        <Route path="/admin/css-editor" element={<CssEditorPage />} />
        <Route path="/admin/support" element={<SupportAdminPage />} />
         

        <Route path="/admin/cms/landing" element={<LandingHtmlEditor />} />
        {/* outer fallback – truly unknown routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

export default React.memo(DynamicRoutes);
